import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { StaffMember } from '@/types'
import { teamApi } from '@/services/teamApi'

interface StaffPickerProps {
  username?: string
  teamId?: string
  value: string | null
  onChange: (userId: string | null) => void
  allowAny?: boolean
  label?: string
}

export const StaffPicker: React.FC<StaffPickerProps> = ({
  username,
  teamId,
  value,
  onChange,
  allowAny = true,
  label,
}) => {
  const { t } = useTranslation('booking')
  const [staff, setStaff] = useState<StaffMember[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const data = teamId
          ? await teamApi.getStaff(teamId)
          : username
            ? await teamApi.getPublicStaff(username)
            : []
        if (!cancelled) setStaff(data)
      } catch {
        if (!cancelled) setStaff([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [username, teamId])

  if (loading || staff.length <= 1) {
    return null
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-foreground">
        {label || t('staff.prefer', { defaultValue: 'Preferred staff' })}
      </label>
      <select
        className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
      >
        {allowAny && (
          <option value="">{t('staff.noPreference', { defaultValue: 'No preference (auto-assign)' })}</option>
        )}
        {staff.map((member) => (
          <option key={member.id} value={member.id}>
            {member.full_name || member.username}
          </option>
        ))}
      </select>
    </div>
  )
}
